import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import { useBranding } from "./BrandingContext";
import { useTheme } from "./ThemeContext";
import {
  FiArrowLeft, FiAlertTriangle, FiSave, FiRefreshCw, FiCheckCircle,
  FiXCircle, FiClock, FiLink, FiToggleLeft, FiToggleRight, FiChevronDown, FiChevronUp,
} from "react-icons/fi";

// Custom Webhook Icon
const WebhookIcon = ({ size = 18, color }) => (
  <svg viewBox="0 0 24 24" stroke={color || "currentColor"} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" fill="none" width={size} height={size}>
    <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
    <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
    <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
  </svg>
);

export default function WebhooksPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { theme } = useTheme();
  const dark = theme === "dark";

  const [config, setConfig] = useState({ url: "", headers: {}, is_active: false });
  const [logs, setLogs] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");
  const [expandedLog, setExpandedLog] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState({});
  const [headerInput, setHeaderInput] = useState("");

  const accentColor = branding?.accentColor || "#f59e0b";

  const bg = dark ? "#0f172a" : "#f8fafc";
  const card = dark ? "#1e293b" : "#ffffff";
  const border = dark ? "#334155" : "#e2e8f0";
  const text = dark ? "#f1f5f9" : "#1e293b";
  const muted = dark ? "#94a3b8" : "#64748b";

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${API_BASE_URL}/webhooks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(res.data);
      // Convert headers object to editable string
      setHeaderInput(
        Object.entries(res.data.headers || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n")
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load webhook config.");
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${API_BASE_URL}/webhooks/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data || []);
    } catch {
      // logs are non-critical
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchLogs();
  }, [fetchConfig, fetchLogs]);

  const parseHeaders = (raw) => {
    const result = {};
    raw.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim();
        if (key) result[key] = val;
      }
    });
    return result;
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaveSuccess(false);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(
        `${API_BASE_URL}/webhooks`,
        {
          url: config.url,
          headers: parseHeaders(headerInput),
          is_active: config.is_active,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      fetchConfig();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save webhook config.");
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (logId) => {
    setResendingId(logId);
    setResendMsg((prev) => ({ ...prev, [logId]: null }));
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(
        `${API_BASE_URL}/webhooks/resend`,
        { log_id: logId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResendMsg((prev) => ({ ...prev, [logId]: { ok: true, msg: res.data.message } }));
      fetchLogs();
    } catch (err) {
      setResendMsg((prev) => ({
        ...prev,
        [logId]: { ok: false, msg: err.response?.data?.error || "Resend failed." },
      }));
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: bg, color: text, fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ background: card, borderBottom: `1px solid ${border}`, padding: "16px 32px", display: "flex", alignItems: "center", gap: "16px" }}>
        <button
          onClick={() => navigate("/dashboard")}
          style={{ background: "none", border: "none", color: muted, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}
        >
          <FiArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ width: "1px", height: "20px", background: border }} />
        <WebhookIcon size={20} color={accentColor} />
        <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Webhooks</h1>
      </div>

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "40px 24px" }}>

        {error && (
          <div style={{ background: dark ? "#2d1515" : "#fef2f2", border: `1px solid ${dark ? "#7f1d1d" : "#fecaca"}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "24px", display: "flex", gap: "10px", alignItems: "center" }}>
            <FiAlertTriangle size={16} style={{ color: "#ef4444" }} />
            <span style={{ fontSize: "14px", color: "#ef4444" }}>{error}</span>
          </div>
        )}

        {/* Config Card */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: "16px", padding: "28px", marginBottom: "32px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
            <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
              <FiLink size={16} style={{ color: muted }} /> Endpoint Configuration
            </h2>
            <button
              onClick={() => setConfig((c) => ({ ...c, is_active: !c.is_active }))}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", color: config.is_active ? "#22c55e" : muted }}
            >
              {config.is_active ? <FiToggleRight size={22} /> : <FiToggleLeft size={22} />}
              {config.is_active ? "Active" : "Inactive"}
            </button>
          </div>

          {loadingConfig ? (
            <p style={{ color: muted, fontSize: "14px" }}>Loading configuration...</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: muted, marginBottom: "8px" }}>
                  Webhook URL
                </label>
                <input
                  type="url"
                  placeholder="https://your-server.com/webhooks/signtada"
                  value={config.url}
                  onChange={(e) => setConfig((c) => ({ ...c, url: e.target.value }))}
                  style={{
                    width: "100%", padding: "10px 14px", borderRadius: "10px",
                    border: `1px solid ${border}`, background: dark ? "#0f172a" : "#f8fafc",
                    color: text, fontSize: "14px", outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: muted, marginBottom: "8px" }}>
                  Custom Headers <span style={{ fontWeight: 400 }}>(one per line, format: Key: Value)</span>
                </label>
                <textarea
                  rows={4}
                  placeholder={"Authorization: Bearer your-secret\nX-Custom-Header: value"}
                  value={headerInput}
                  onChange={(e) => setHeaderInput(e.target.value)}
                  style={{
                    width: "100%", padding: "10px 14px", borderRadius: "10px",
                    border: `1px solid ${border}`, background: dark ? "#0f172a" : "#f8fafc",
                    color: text, fontSize: "13px", fontFamily: "monospace", resize: "vertical",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: "10px 22px", borderRadius: "10px", border: "none", cursor: saving ? "not-allowed" : "pointer",
                    background: `linear-gradient(135deg, ${accentColor}, #d97706)`, color: "#000",
                    fontWeight: 600, fontSize: "14px", opacity: saving ? 0.7 : 1,
                    display: "flex", alignItems: "center", gap: "8px",
                  }}
                >
                  <FiSave size={15} /> {saving ? "Saving..." : "Save Configuration"}
                </button>
                {saveSuccess && (
                  <span style={{ fontSize: "14px", color: "#22c55e", display: "flex", alignItems: "center", gap: "6px" }}>
                    <FiCheckCircle size={16} /> Saved!
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Webhook Logs */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: "10px" }}>
            <FiClock size={16} style={{ color: muted }} />
            <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 600 }}>Recent Deliveries</h2>
            <button
              onClick={fetchLogs}
              title="Refresh logs"
              style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: muted, padding: "4px", display: "flex" }}
            >
              <FiRefreshCw size={15} />
            </button>
          </div>

          {loadingLogs ? (
            <div style={{ padding: "48px", textAlign: "center", color: muted }}>Loading logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: "64px 32px", textAlign: "center" }}>
              <WebhookIcon size={40} color={muted} />
              <p style={{ color: muted, marginTop: "16px" }}>No webhook deliveries yet.</p>
            </div>
          ) : (
            <div>
              {logs.map((log, idx) => {
                const isExpanded = expandedLog === log._id;
                const success = log.status_code >= 200 && log.status_code < 300;
                return (
                  <div key={log._id} style={{ borderBottom: idx < logs.length - 1 ? `1px solid ${border}` : "none" }}>
                    <div
                      style={{ padding: "16px 28px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", flexWrap: "wrap" }}
                      onClick={() => setExpandedLog(isExpanded ? null : log._id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {success
                          ? <FiCheckCircle size={15} style={{ color: "#22c55e" }} />
                          : <FiXCircle size={15} style={{ color: "#ef4444" }} />
                        }
                        <span style={{
                          fontSize: "12px", fontWeight: 600, color: success ? "#22c55e" : "#ef4444",
                          background: success ? (dark ? "#14261e" : "#f0fdf4") : (dark ? "#2d1515" : "#fef2f2"),
                          padding: "2px 8px", borderRadius: "20px",
                        }}>
                          {log.status_code || "N/A"}
                        </span>
                      </div>

                      <span style={{ fontSize: "13px", color: muted, fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {log.event_type}
                      </span>

                      <span style={{ fontSize: "12px", color: muted }}>
                        <FiClock size={11} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                        {formatDate(log.timestamp)}
                      </span>

                      <button
                        onClick={(e) => { e.stopPropagation(); handleResend(log._id); }}
                        disabled={resendingId === log._id}
                        style={{
                          padding: "5px 12px", borderRadius: "7px", fontSize: "12px", fontWeight: 500,
                          background: dark ? "#1e293b" : "#f1f5f9", border: `1px solid ${border}`,
                          color: text, cursor: resendingId === log._id ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", gap: "5px",
                        }}
                        title="Resend this webhook"
                      >
                        <FiRefreshCw size={12} /> {resendingId === log._id ? "Sending..." : "Resend"}
                      </button>

                      {isExpanded ? <FiChevronUp size={15} style={{ color: muted }} /> : <FiChevronDown size={15} style={{ color: muted }} />}
                    </div>

                    {resendMsg[log._id] && (
                      <div style={{ padding: "0 28px 12px" }}>
                        <span style={{ fontSize: "12px", color: resendMsg[log._id].ok ? "#22c55e" : "#ef4444" }}>
                          {resendMsg[log._id].msg}
                        </span>
                      </div>
                    )}

                    {isExpanded && (
                      <div style={{ padding: "0 28px 20px" }}>
                        <pre style={{
                          background: dark ? "#0f172a" : "#f8fafc", borderRadius: "10px", padding: "14px",
                          fontSize: "12px", color: muted, overflow: "auto", maxHeight: "300px",
                          border: `1px solid ${border}`, margin: 0,
                        }}>
                          {JSON.stringify(log.payload || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
